import { cn } from '../lib/utils'
import type { NuxPlacement, NuxTooltipCopy } from './nuxCatalog'

export interface NuxTooltipPosition {
  top: number
  left: number
  placement: NuxPlacement
}

interface NuxTooltipProps {
  id: string
  copy: NuxTooltipCopy
  position: NuxTooltipPosition
  onSkip: () => void
}

const CARET_CLASS: Record<NuxPlacement, string> = {
  top: 'left-1/2 top-full -translate-x-1/2 border-x-8 border-t-8 border-x-transparent border-t-border',
  right: 'right-full top-1/2 -translate-y-1/2 border-y-8 border-r-8 border-y-transparent border-r-border',
  bottom: 'bottom-full left-1/2 -translate-x-1/2 border-x-8 border-b-8 border-x-transparent border-b-border',
  left: 'left-full top-1/2 -translate-y-1/2 border-y-8 border-l-8 border-y-transparent border-l-border',
}

const CARET_INNER_CLASS: Record<NuxPlacement, string> = {
  top: 'left-1/2 top-[calc(100%-1px)] -translate-x-1/2 border-x-[7px] border-t-[7px] border-x-transparent border-t-surface-1',
  right: 'right-[calc(100%-1px)] top-1/2 -translate-y-1/2 border-y-[7px] border-r-[7px] border-y-transparent border-r-surface-1',
  bottom: 'bottom-[calc(100%-1px)] left-1/2 -translate-x-1/2 border-x-[7px] border-b-[7px] border-x-transparent border-b-surface-1',
  left: 'left-[calc(100%-1px)] top-1/2 -translate-y-1/2 border-y-[7px] border-l-[7px] border-y-transparent border-l-surface-1',
}

export function NuxTooltip({ id, copy, position, onSkip }: NuxTooltipProps) {
  const titleId = `${id}-title`
  const bodyId = `${id}-body`

  return (
    <div
      className="fixed z-[170] w-[min(360px,calc(100vw-32px))]"
      style={{ top: position.top, left: position.left }}
      data-testid="nux-tooltip"
      data-nux-tooltip-id={id}
      data-click-outside-ignore
      data-interaction-layer="nux-tooltip"
    >
      <div
        role="dialog"
        aria-modal="false"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        onKeyDown={(event) => {
          if (event.defaultPrevented || event.key !== 'Escape') return
          event.preventDefault()
          event.stopPropagation()
          onSkip()
        }}
        className="relative rounded-xl border border-border bg-surface-1 px-6 pb-5 pt-5 shadow-2xl"
      >
        <span className={cn('absolute h-0 w-0', CARET_CLASS[position.placement])} />
        <span className={cn('absolute h-0 w-0', CARET_INNER_CLASS[position.placement])} />
        <div className="min-w-0">
          <h2 id={titleId} className="text-lg font-semibold leading-6 text-foreground">
            {copy.title}
          </h2>
          <p id={bodyId} className="mt-2 text-base leading-6 text-muted-foreground">
            {copy.body}
          </p>
        </div>
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onSkip}
            className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-white transition hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            data-testid="nux-tooltip-skip"
          >
            {copy.skip}
          </button>
        </div>
      </div>
    </div>
  )
}
